/**
 * Translates a subscription selection into the shared `<CheckoutShell>`
 * `Cart` value object.
 *
 * Pure function. No React, no API calls. The adapter component composes
 * this with the selection from SubscriptionCheckoutContext (populated
 * by the landing page from the offering API), then hands the result to
 * the shell.
 *
 * Subscription carts are intentionally minimal:
 *   - One CartLine for the chosen plan
 *   - No discount lines
 *   - No shipping, no vouchers, no promo codes, no admin discounts
 *   - subtotal === total === unitPrice
 *
 * The shell's `paymentMode` is set by the adapter to "picker-required",
 * not by this function — the cart shape itself is payment-mode-agnostic.
 */

import { Cart, CartLine } from "@/modules/checkout/types";
import type { SubscriptionSelection } from "../SubscriptionCheckoutContext";

export type BuildSubscriptionCartInput = {
  selection: SubscriptionSelection;
};

export function buildSubscriptionCart(input: BuildSubscriptionCartInput): Cart {
  const { selection } = input;
  const line: CartLine = {
    sku: selection.caraSku,
    name: selection.offeringName,
    description: `${selection.label} subscription`,
    quantity: 1,
    unitPrice: selection.unitPrice,
    metadata: {
      requiresShipping: false,
      voucherEligible: false,
      discountCodeEligible: false,
    },
    domainData: {
      frequency: selection.frequency,
    },
  };

  return {
    lines: [line],
    discountLines: [],
    subtotal: selection.unitPrice,
    total: selection.unitPrice,
    needsShipping: false,
    acceptsDiscountCode: false,
    acceptsVouchers: false,
    adminCanDiscount: false,
  };
}
